import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Fuse from "fuse.js";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

import Layout from "../components/Layout";
import SearchBar from "../components/SearchBar";
import IndustryIcon from "../components/IndustryIcon";
import { CardSkeleton } from "../components/Skeletons";
import { listIndustries, searchProblems } from "../lib/api";

export default function HomePage() {
  const [industries, setIndustries] = useState([]);
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    Promise.all([listIndustries(), searchProblems()])
      .then(([inds, probs]) => {
        if (!mounted) return;
        setIndustries(inds);
        setProblems(probs);
      })
      .catch((e) => {
        if (mounted) setError(e?.response?.data?.detail || e.message);
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const fuse = useMemo(
    () =>
      new Fuse(problems, {
        keys: ["problem_title", "industry_name"],
        threshold: 0.32,
        ignoreLocation: true,
      }),
    [problems]
  );

  const matches = useMemo(() => {
    if (!query.trim()) return [];
    return fuse.search(query).slice(0, 8).map((r) => r.item);
  }, [query, fuse]);

  return (
    <Layout>
      {/* HERO — briefing-paper style */}
      <section
        className="relative border-b border-ink-200 paper-grain"
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-16 md:pb-20 grid md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-8">
            <div className="flex items-center gap-3 mb-10">
              <span className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500">
                A reference library · Edition 01
              </span>
              <span className="hairline w-8" />
              <span className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500">
                Updated continuously
              </span>
            </div>
            <h1 className="font-heading font-medium leading-[1.06] tracking-[-0.02em] text-ink-900 text-4xl sm:text-5xl lg:text-6xl">
              Strategic frameworks,<br />
              recommended by problem,<br />
              <span className="text-ink-500">translated to industry.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-base md:text-lg text-ink-600 leading-relaxed">
              A working reference for senior consultants and strategy practitioners. Select an industry, identify the problem on your desk, and receive a single recommended framework — rewritten in the vocabulary, examples, and decision context of that sector.
            </p>
            <div className="mt-10 max-w-2xl">
              <SearchBar
                value={query}
                onChange={setQuery}
                placeholder="Search 1,500 documented problems by keyword or industry…"
                testId="global-search"
              />
              {query && matches.length > 0 && (
                <div
                  className="mt-3 border border-ink-200 bg-bone shadow-[0_8px_32px_rgba(0,0,0,0.05)]"
                  data-testid="global-search-results"
                >
                  {matches.map((m) => (
                    <button
                      key={`${m.industry_slug}-${m.problem_slug}`}
                      onClick={() =>
                        navigate(`/${m.industry_slug}/${m.problem_slug}`)
                      }
                      className="w-full text-left px-5 py-3.5 border-b border-ink-100 last:border-b-0 hover:bg-ink-50 transition-colors group"
                      data-testid={`global-search-result-${m.problem_id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-ink-900 leading-snug">
                            {m.problem_title}
                          </div>
                          <div className="mt-1 font-mono text-[10px] text-ink-500 uppercase tracking-wide-2">
                            {m.industry_name}
                          </div>
                        </div>
                        <ArrowRight
                          size={14}
                          className="text-ink-400 group-hover:text-ink-900 mt-1 transition-colors"
                        />
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {query && matches.length === 0 && !loading && (
                <p className="mt-3 font-mono text-[11px] uppercase tracking-wide-2 text-ink-500" data-testid="global-search-empty">
                  No matches for &ldquo;{query}&rdquo;
                </p>
              )}
            </div>
          </div>

          <aside className="md:col-span-4 hidden md:flex flex-col justify-end">
            <div className="border-l border-ink-200 pl-8">
              <p className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500">
                The arithmetic
              </p>
              <dl className="mt-6 space-y-5">
                {[
                  ["60", "Strategic frameworks indexed"],
                  ["50", "Industries covered"],
                  ["1,500", "Problem ↦ framework recommendations"],
                  ["13", "Distinct diagram archetypes"],
                ].map(([n, label]) => (
                  <div key={n} className="flex items-baseline gap-5">
                    <dt className="font-heading text-4xl font-medium text-ink-900 tabular-nums leading-none">
                      {n}
                    </dt>
                    <dd className="text-[13px] text-ink-600 leading-snug">
                      {label}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </aside>
        </div>
      </section>

      {/* INDUSTRIES — directory-style grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500">
              Section 01 · Directory
            </p>
            <h2 className="mt-3 font-heading text-2xl md:text-3xl font-medium text-ink-900 tracking-tight">
              Browse by industry vertical.
            </h2>
          </div>
          {!loading && (
            <p className="font-mono text-[11px] uppercase tracking-wide-2 text-ink-500" data-testid="industry-count">
              {problems.length} problems documented
            </p>
          )}
        </div>

        {error && (
          <div
            className="border border-ink-300 bg-ink-100 text-ink-900 p-6 mb-8"
            data-testid="home-error"
          >
            <p className="font-medium">Unable to load the directory.</p>
            <p className="text-sm text-ink-600 mt-1">{String(error)}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink-200 border border-ink-200">
          {loading
            ? Array.from({ length: 9 }).map((_, i) => (
                <div key={i} className="bg-bone">
                  <CardSkeleton />
                </div>
              ))
            : industries.map((ind, idx) => (
                <motion.div
                  key={ind.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: Math.min(idx * 0.015, 0.25) }}
                  className="bg-bone"
                >
                  <Link
                    to={`/${ind.slug}`}
                    className="group flex flex-col p-7 h-full transition-colors duration-200 hover:bg-ink-50"
                    data-testid={`industry-card-${ind.slug}`}
                  >
                    <div className="flex items-center justify-between mb-7">
                      <div className="inline-flex p-2.5 bg-ink-100 text-ink-900 rounded-sm">
                        <IndustryIcon name={ind.icon} size={18} />
                      </div>
                      <span className="font-mono text-[10px] uppercase tracking-wide-2 text-ink-400">
                        IND.{String(ind.id).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="font-heading text-lg font-medium text-ink-900 leading-snug tracking-tight">
                      {ind.name}
                    </h3>
                    <p className="mt-3 text-[13px] text-ink-600 leading-relaxed line-clamp-3 flex-1">
                      {ind.description}
                    </p>
                    <div className="mt-6 flex items-center justify-between">
                      <span className="font-mono text-[10px] uppercase tracking-wide-2 text-ink-500">
                        30 problems
                      </span>
                      <ArrowRight
                        size={14}
                        className="text-ink-400 group-hover:text-ink-900 group-hover:translate-x-1 transition-all"
                      />
                    </div>
                  </Link>
                </motion.div>
              ))}
        </div>
      </section>

      {/* HOW IT WORKS — methodology bar */}
      <section className="border-y border-ink-200 bg-bone">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 grid md:grid-cols-3 gap-10">
          {[
            [
              "01",
              "Select",
              "Choose your client&rsquo;s industry vertical from the directory.",
            ],
            [
              "02",
              "Identify",
              "Find the closest match among the thirty documented problems for that vertical.",
            ],
            [
              "03",
              "Apply",
              "Receive a single recommended framework, rewritten in the language of that industry.",
            ],
          ].map(([num, title, body]) => (
            <div key={num} className="flex gap-5">
              <span className="font-mono text-[11px] uppercase tracking-wide-2 text-vermilion shrink-0 pt-1">
                {num}
              </span>
              <div>
                <h3 className="font-heading text-lg font-medium text-ink-900 tracking-tight">
                  {title}
                </h3>
                <p className="mt-2 text-sm text-ink-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: body }} />
              </div>
            </div>
          ))}
        </div>
      </section>
    </Layout>
  );
}
